import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  MapPin, ArrowRight, Check,
  Target,
} from 'phosphor-react';
import { motion, AnimatePresence } from 'framer-motion';
import SBack from '@/components/ui/molecules/SBack';
import SLoader from '@/components/ui/atoms/SLoader';
import { ThemeToggle } from '@/components/ui/atoms/ThemeToggle';
import { HittingBayIcon, PrivateHittingBayIcon, ClubFittingIcon, RecoveryRoomIcon, PuttViewIcon, WorkspaceIcon, GymIcon, BoxAssessmentIcon, ConferenceRoomIcon } from '@/components/ui/atoms/CustomIcon';
import { EntitlementPill } from '@/components/ui/atoms/EntitlementPill';
import { useUIBookingTypes } from '@/hooks/useBookingTypes';
import { useUserInfo } from '@/hooks/useUserInfo';
import { useUserLocation } from '@/hooks/useUserLocation';
import { BookingTypeUI, FacilityType } from '@/types/booking.types';
import { LocationData } from '@/types/location.types';
import './BookingType.css';

// ─── Facility icon — Figma custom icons with Phosphor fallbacks ────────────

const CUSTOM_ICONS: Partial<Record<FacilityType, React.FC<{ size?: number }>>> = {
  [FacilityType.HITTING_BAY]:         HittingBayIcon,
  [FacilityType.PRIVATE_HITTING_BAY]: PrivateHittingBayIcon,
  [FacilityType.CLUB_FITTING]:        ClubFittingIcon,
  [FacilityType.RECOVERY_ROOM]:       RecoveryRoomIcon,
  [FacilityType.PUTT_VIEW]:           PuttViewIcon,
  [FacilityType.WORKSPACE]:           WorkspaceIcon,
  [FacilityType.GYM]:                 GymIcon,
  [FacilityType.BOX_ASSESSMENT]:      BoxAssessmentIcon,
  [FacilityType.CONFERENCE_ROOM]:     ConferenceRoomIcon,
};

const PHOSPHOR_FALLBACKS: Partial<Record<FacilityType, React.ElementType>> = {};

function FacilityIcon({ type }: { type: FacilityType }) {
  const Custom = CUSTOM_ICONS[type];
  if (Custom) return <Custom size={36} />;
  const Icon = PHOSPHOR_FALLBACKS[type] ?? Target;
  return <Icon size={26} weight="duotone" />;
}

// ─── Component ────────────────────────────────────────────────────────────────

const BookingType: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [selectedType, setSelectedType] = useState<BookingTypeUI | null>(null);
  const [selectedLocation, setSelectedLocation] = useState<LocationData | null>(null);

  const { user, locationId: userLocationId } = useUserInfo();
  const { location: userLocation } = useUserLocation();

  useEffect(() => {
    const fromState = location.state?.selectedLocation;
    if (fromState) { setSelectedLocation(fromState); return; }
    if (userLocation) {
      setSelectedLocation({
        id: userLocation.id,
        name: userLocation.name,
        address: `${userLocation.address.line1}, ${userLocation.address.city}, ${userLocation.address.state} ${userLocation.address.zip}`,
        timezone: userLocation.timezone,
        bookingStartDate: userLocation.bookingStartDate,
      });
    }
  }, [location.state, userLocation, userLocationId]);

  const { data: bookingTypes = [], isLoading, error } = useUIBookingTypes(selectedLocation?.id);
  const activeTypes = bookingTypes.filter(t => t.active !== false);

  // Presale members cannot book at a location with no Booking Start Date set
  // yet, or whose Booking Start Date hasn't arrived — mirrors the API guard
  // in BookingsService.assertPresaleBookingWindowOpen (quick task 260918-8hg).
  // Compares the raw status string since useUserInfo's UserData types
  // `status` as `string`, not the client MemberStatus enum.
  const isPresaleBookingBlocked =
    user?.status === 'presale' &&
    (!selectedLocation?.bookingStartDate ||
      new Date(selectedLocation.bookingStartDate) > new Date());

  const handleProceed = () => {
    if (isPresaleBookingBlocked) return;
    if (!selectedType) return;
    navigate('/booking-slots', {
      state: {
        bookingTypeId: selectedType.id,
        bookingTypeName: selectedType.title,
        bookingType: selectedType,
        selectedLocation,
      },
    });
  };

  const locationDisplayName = selectedLocation?.name
    ? `Tee Box · ${selectedLocation.name}`
    : 'Select a location';

  return (
    <div className="bt">
      {/* Header */}
      <div className="bt__header">
        <SBack onClick={() => navigate('/home', { replace: true })} />
        <ThemeToggle />
      </div>

      {/* Body */}
      <div className="bt__body">
        <h1 className="bt__heading">Select Booking Type</h1>

        {/* Location */}
        <div className="bt__location">
          <MapPin size={16} color="#c99535" weight="fill" />
          <div className="bt__location-text">
            <span className="bt__location-label">Location</span>
            <span className="bt__location-name">{locationDisplayName}</span>
          </div>
          <button className="bt__location-change" onClick={() => navigate('/booking-type/location', { state: { selectedLocation } })}>
            Change
          </button>
        </div>

        {/* Card list */}
        {isPresaleBookingBlocked ? (
          <div className="bt__state">Booking isn&apos;t open at this location yet. We&apos;ll let you know as soon as it is.</div>
        ) : isLoading ? (
          <div className="bt__state"><SLoader /></div>
        ) : error ? (
          <div className="bt__state">Failed to load session types. Please try again.</div>
        ) : activeTypes.length === 0 ? (
          <div className="bt__state">No sessions available at this location.</div>
        ) : (
          <div className="bt__list">
            {activeTypes.map((type, i) => {
              const isSelected = selectedType?.id === type.id;
              return (
                <motion.button
                  key={type.id}
                  className={`bt__card ${isSelected ? 'bt__card--selected' : ''}`}
                  onClick={() => setSelectedType(isSelected ? null : type)}
                  initial={{ opacity: 0, y: 12 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.22, delay: i * 0.06, ease: 'easeOut' }}
                  layout
                >
                  {/* Thumbnail */}
                  <div className="bt__thumb">
                    <FacilityIcon type={type.facilityType} />
                  </div>

                  {/* Info */}
                  <div className="bt__card-info">
                    <span className="bt__card-title">{type.title}</span>
                    {type.viaEntitlement && <EntitlementPill remainingCount={type.remainingCount} />}
                  </div>

                  {/* Radio */}
                  <div className="bt__radio">
                    <AnimatePresence>
                      {isSelected && (
                        <motion.div
                          initial={{ scale: 0 }}
                          animate={{ scale: 1 }}
                          exit={{ scale: 0 }}
                          transition={{ duration: 0.15, ease: 'easeOut' }}
                        >
                          <Check size={13} color="#0b0b0a" weight="bold" />
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </div>
                </motion.button>
              );
            })}
          </div>
        )}
      </div>

      {/* Footer CTA */}
      <div className="bt__footer">
        <motion.button
          className="bt__cta"
          onClick={handleProceed}
          disabled={!selectedType || isPresaleBookingBlocked}
          animate={{ opacity: selectedType && !isPresaleBookingBlocked ? 1 : 0.45 }}
          transition={{ duration: 0.2 }}
        >
          Pick Date & Time
          <ArrowRight size={16} weight="bold" />
        </motion.button>
      </div>
    </div>
  );
};

export default BookingType;
